import React, { useState, useEffect, useRef, forwardRef } from 'react';
import { FaUser, FaTimes } from 'react-icons/fa';

interface AutocompleteOption {
  value: string;
  label: string;
}

interface AutocompleteInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  options: AutocompleteOption[];
  onChange: (value: string) => void;
  onSelect?: (option: AutocompleteOption) => void;
  placeholder?: string;
  className?: string;
  loading?: boolean;
}

export const AutocompleteInput = forwardRef<HTMLInputElement, AutocompleteInputProps>(({
  options,
  onChange,
  onSelect,
  placeholder = "입력하세요...",
  className = "",
  loading = false,
  value = "",
  ...props
}, ref) => {
  const [inputValue, setInputValue] = useState(value?.toString() || "");
  const [filteredOptions, setFilteredOptions] = useState<AutocompleteOption[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 입력값에 따른 옵션 필터링
  useEffect(() => {
    if (inputValue.trim() === "") {
      setFilteredOptions([]);
      setShowDropdown(false);
      return;
    }

    const filtered = options.filter(option =>
      option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
      option.value.toLowerCase().includes(inputValue.toLowerCase())
    );

    setFilteredOptions(filtered);
    setShowDropdown(filtered.length > 0);
    setSelectedIndex(-1);
  }, [inputValue, options]);

  // 외부 클릭 시 드롭다운 닫기
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // 입력값 변경 처리
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    onChange(newValue);
  };

  // 옵션 선택 처리
  const handleOptionSelect = (option: AutocompleteOption) => {
    setInputValue(option.label);
    setShowDropdown(false);
    onChange(option.value);
    if (onSelect) {
      onSelect(option);
    }
  };

  // 키보드 네비게이션
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showDropdown || filteredOptions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredOptions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < filteredOptions.length) {
          handleOptionSelect(filteredOptions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        setSelectedIndex(-1);
        break;
    }
  };

  // 입력값 클리어
  const handleClear = () => {
    setInputValue("");
    onChange("");
    setShowDropdown(false);
  };

  return (
    <div ref={containerRef} className="relative">
      <div className="relative">
        <input
          ref={ref}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          lang="ko"
          inputMode="text"
          className={`w-full px-3 py-2 pr-20 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white ${className}`}
          autoComplete="off"
          {...props}
        />
        
        {/* 아이콘 및 클리어 버튼 */}
        <div className="absolute right-2 top-1/2 transform -translate-y-1/2 flex items-center space-x-1">
          {inputValue && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            >
              <FaTimes className="h-3 w-3" />
            </button>
          )}
          <FaUser className="h-4 w-4 text-gray-400" />
        </div>
      </div>

      {/* 드롭다운 목록 */}
      {showDropdown && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-y-auto"
        >
          {loading && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
              로딩 중...
            </div>
          )}
          
          {!loading && filteredOptions.length === 0 && inputValue.trim() !== "" && (
            <div className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 text-center">
              일치하는 결과가 없습니다.
            </div>
          )}

          {!loading && filteredOptions.map((option, index) => (
            <button
              key={`${option.value}-${index}`}
              type="button"
              onClick={() => handleOptionSelect(option)}
              className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition-colors ${
                index === selectedIndex 
                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-900 dark:text-blue-200' 
                  : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="flex items-center">
                <FaUser className="h-3 w-3 mr-2 text-gray-400" />
                <span className="font-medium">{option.label}</span>
                {option.value !== option.label && (
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
                    ({option.value})
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

AutocompleteInput.displayName = 'AutocompleteInput';